import { Link } from 'react-router-dom';
import {
  Headphones,
  ShieldAlert,
  ScrollText,
  Workflow,
  Wallet,
  BookOpen,
  Brain,
  Server,
  Boxes,
  Building2
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';

/**
 * Admin home — a portal-style index card grid. The actual ops content
 * lives on each sub-screen. Keep this lightweight so it loads fast and
 * staff can keyboard-navigate to whatever they're after.
 */
const TILES = [
  {
    to: '/customer-service',
    label: 'Customer Service',
    icon: Headphones,
    desc: 'Global search across publishers + devices. Balance adjustments, device detail, publisher detail.'
  },
  {
    to: '/escalations',
    label: 'Escalations',
    icon: ShieldAlert,
    desc: 'Queue from prospecting, outreach, registration, integration, customer service, inference signup.'
  },
  {
    to: '/audit-log',
    label: 'Audit Log',
    icon: ScrollText,
    desc: 'Every admin action — auth, user, device, payout, component, system, support changes.'
  },
  {
    to: '/pipeline',
    label: 'Onboarding Pipeline',
    icon: Workflow,
    desc: 'Prospects, outreach sequences, onboarding states, the agentic registration funnel.'
  },
  {
    to: '/crypto-assets',
    label: 'Crypto Assets',
    icon: Wallet,
    desc: 'Wallet overview, publisher balances, withdrawals, multi-provider exchange execution.'
  },
  {
    to: '/cap-table',
    label: 'Cap Table',
    icon: BookOpen,
    desc: 'Shareholders, share classes, share transactions, shareholder documents.'
  },
  {
    to: '/inference',
    label: 'Inference Admin',
    icon: Brain,
    desc: 'Inference customer CRUD, API key management, usage, jobs. Same data console.nurev.io shows the buyer.'
  },
  {
    to: '/infrastructure',
    label: 'Infrastructure',
    icon: Server,
    desc: 'Health, components, sessions timeline. Real-time platform-level metrics.'
  },
  {
    to: '/components',
    label: 'Components',
    icon: Boxes,
    desc: 'Per-component manifest CRUD, live session counts, GPU + storage + bandwidth wiring.'
  },
  {
    to: '/integrations',
    label: 'Integrations',
    icon: Building2,
    desc: 'Per-publisher integration types, downstream service binding, super-publisher channel config.'
  }
];

export function OverviewScreen() {
  const admin = useAuthStore(s => s.admin);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-nurev-text">
          {admin ? `Hey ${admin.name.split(' ')[0]}.` : 'Admin Console'}
        </h1>
        <p className="mt-1 text-sm text-nurev-muted">
          Pick a tool. All routes are admin-only (
          <code className="text-amber-400">role: 'admin'</code>) and audit-logged.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {TILES.map(t => (
          <Link
            key={t.to}
            to={t.to}
            className="group rounded-xl border border-nurev-border bg-nurev-card p-5 transition hover:border-amber-500/40 hover:bg-amber-500/[0.03]"
          >
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 text-amber-300 ring-1 ring-amber-500/30 transition group-hover:bg-amber-500/20">
                <t.icon className="h-4 w-4" />
              </div>
              <h2 className="font-semibold text-nurev-text">{t.label}</h2>
            </div>
            <p className="text-xs leading-relaxed text-nurev-muted">{t.desc}</p>
          </Link>
        ))}
      </div>

      <div className="rounded-xl border border-nurev-border bg-white/[0.02] p-4 text-xs text-nurev-muted">
        <strong className="text-nurev-text">Note:</strong> some tools above
        currently link out to <code>app.nurev.io</code> until their UI is
        ported into this surface. They're tagged "Migration pending" on the
        target screen. Same backend, same data — just hosted on the publisher
        dashboard for now. See{' '}
        <code>nurev-api/docs/FLEET_CONSOLE_SCOPE.md</code> for the carve-off
        pattern.
      </div>
    </div>
  );
}
